import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CHAT_BOT } from '__queries/query-keys';
import MachineLearningService from 'services/MachineLearningService';
import { toastVariant, useToast } from '__components/ToastProvider';
import { deferExecution } from 'utility/defer';
import { ChatBot } from 'ts-components/ChatBot';
import { OutputVariableRequirements } from 'ts-components/ChatBot/types';
import { FlowStatusFromAPI } from 'ts-components/ChatBot/types';
import { Indicator } from './Indicator';
import { BusinessGenerationSummary } from 'ts-components/BusinessGenerationSummary';
import { QuickResponseData } from 'ts-components/QuickResponses';

export const GUARD_RAIL_CODES = {
  REQUIRES_REVIEW: 'REQUIRES_REVIEW',
};

export const FLOW_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

export const FLOW_TYPE = {
  email_builder: 'email_builder',
  business_builder: 'business_builder',
  custom_object_editor: 'custom_object_editor',
  form_builder: 'form_builder',
  filter_builder_inline: 'filter_builder_inline',
  homepage_builder_inline: 'homepage_builder_inline',
  dashboard_builder_inline: 'dashboard_builder_inline',
  survey_builder: 'survey_builder',
  code_step_generation: 'code_step_generation',
  automation_builder_inline: 'automation_builder_inline',
  chart_group_builder_inline: 'chart_group_builder_inline',
  llm_step_generation: 'llm_step_generation',
  dashlet_builder_inline: 'dashlet_builder_inline',
} as const;

export const TASK_TYPE = {
  FORM_PLAN_GENERATION: 'form_plan_generation',
  EMAIL_PLAN_GENERATION: 'email_plan_generation',
  CUSTOM_OBJECT_EDITOR_PLANNER: 'custom_object_editor_planner',
  BUSINESS_STATEGIC_PLAN_GENERATION: 'business_strategic_plan_generation',
  SURVEY_PLAN_GENERATION: 'survey_plan_generation',
  AUTOMATION_PLAN_GENERATION: 'automation_planner',
  DASHLET_PLAN_GENERATION: 'dashlet_planner',
};

type ConversationProps = {
  showLogo?: boolean;
  headerLogoUrl?: string;
  header?: string;
  isMobile?: boolean;
  enabled?: boolean;
  inModal?: boolean;
  context?: Record<string, string>;
  objectId?: string;
  flowType?: string;
  taskType?: string;
  onComplete?: (id: string) => void;
  onError?: (error: any) => void;
  onSessionChange?: (chatSession: string) => void;
  entityName?: string;
  pendingMessages?: Array<string>;
  generationVarToMessageHeaderMapper?: Record<string, string>;
  renderMarkdown?: boolean;
  sampleIdeas?: QuickResponseData;
};

export const Conversation = ({
  enabled = true,
  inModal = false,
  isMobile = false,
  showLogo = false,
  headerLogoUrl,
  header,
  context,
  objectId,
  flowType = FLOW_TYPE.business_builder,
  taskType = TASK_TYPE.BUSINESS_STATEGIC_PLAN_GENERATION,
  onComplete,
  onError,
  onSessionChange,
  entityName,
  pendingMessages = [],
  generationVarToMessageHeaderMapper = {},
  renderMarkdown,
  sampleIdeas,
}: ConversationProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const initialLoadRef = useRef(true);
  const flowStatusCallsRef = useRef(0);
  const [showToast] = useToast();
  const [startedGeneration, setStartedGeneration] = useState(false);

  const fetchChatSession = useCallback(async () => {
    try {
      const results = await MachineLearningService.getNewChatSession(
        taskType,
        context
      );
      return results;
    } catch (e: any) {
      let errorMessage = t('Error fetching chat session');
      const nonFieldErrors = e?.response?.data?.context?.non_field_errors;
      if (nonFieldErrors) {
        errorMessage = nonFieldErrors[0];
      }
      onError?.(e);
      showToast({
        variant: toastVariant.FAILURE,
        message: errorMessage,
      });
      throw e;
    }
  }, [context, taskType, t, showToast, onError]);

  const {
    data: newChatSession,
    isLoading: isNewChatSessionLoading,
    isError: isNewChatSessionError,
    refetch: refetchChatSession,
    isRefetching: isRefetchingChatSession,
  } = useQuery({
    queryKey: CHAT_BOT.NEW_CHAT_SESSION(taskType, context),
    queryFn: fetchChatSession,
    retry: false,
    enabled: enabled,
    throwOnError: false,
  });

  useEffect(() => {
    if (inModal && initialLoadRef.current) {
      refetchChatSession();
    }
  }, [inModal, refetchChatSession]);

  const {
    isLoading: isFlowStatusLoading,
    data: flowStatus,
    refetch: refetchFlowStatus,
    isRefetching: isRefetchingFlowStatus,
  } = useQuery({
    queryKey: CHAT_BOT.FLOW_STATUS(flowType, objectId, newChatSession?.id),
    queryFn: async () => {
      try {
        let flowStatus;
        flowStatus = await MachineLearningService.getFlowStatus({
          flowType,
          objectId,
        });
        flowStatusCallsRef.current++;
        if (inModal) {
          const isFailedCompletedAndFirstCall =
            (flowStatus.status === FLOW_STATUS.FAILED ||
              flowStatus.status === FLOW_STATUS.COMPLETED) &&
            flowStatusCallsRef.current <= 1;

          if (isFailedCompletedAndFirstCall) {
            flowStatus = await MachineLearningService.getFlowStatus({
              flowType,
              objectId,
              chatSessionId: newChatSession?.id,
            });
          } else if (
            flowStatusCallsRef.current >= 2 &&
            (flowStatus?.status === FLOW_STATUS.COMPLETED ||
              flowStatus?.status === FLOW_STATUS.FAILED)
          ) {
            flowStatusCallsRef.current = 0;
            setStartedGeneration(false);
            onComplete?.(flowStatus);
          }
        }
        initialLoadRef.current = false;
        if (
          (flowStatus?.status === FLOW_STATUS.COMPLETED ||
            flowStatus?.status === FLOW_STATUS.FAILED) &&
          !inModal
        ) {
          flowStatusCallsRef.current = 0;
          setStartedGeneration(false);
          onComplete?.(flowStatus);
        }
        return flowStatus;
      } catch (error: any) {
        initialLoadRef.current = true;
        if (error?.response?.status === 400) {
          const errorMessage = t('Error fetching generation status');
          onError?.(error);
          showToast({
            variant: toastVariant.FAILURE,
            message: errorMessage,
          });
        }
        if (error?.response?.status === 404) {
          return {
            status: 'not_started',
          };
        }
        throw error;
      }
    },

    retry: (error: any) => error?.response?.status !== 404,
    refetchInterval: (query: any) => {
      const data = query?.state?.data;
      return data?.status === FLOW_STATUS.IN_PROGRESS ? 2000 : false; // once we complete a flow check every 2 seconds for completion
    },
    enabled: !!newChatSession?.id && !isNewChatSessionLoading && enabled,
  });

  const { data: modelConfigMetadata } = useQuery({
    queryKey: CHAT_BOT.METADATA,
    queryFn: async () => {
      return MachineLearningService.getChatMetadata();
    },
    enabled: true,
  });

  const streamEventListeners = useMemo(() => {
    const eventListenerNames =
      modelConfigMetadata?.[taskType]?.output_variables?.map(
        ({ name }: OutputVariableRequirements) => name
      ) ?? [];
    return eventListenerNames;
  }, [modelConfigMetadata, taskType]);

  const deleteSessionMutation = useMutation({
    mutationFn: async () => {
      flowStatusCallsRef.current = 0;
      await MachineLearningService.cancelChatSession(newChatSession?.id);
      queryClient.setQueryData(CHAT_BOT.NEW_CHAT_SESSION, {});
      refetchChatSession();
    },
  });

  const newChatSessionId = newChatSession?.id;

  // onSessionChange callback
  useEffect(() => {
    if (newChatSessionId) {
      onSessionChange?.(newChatSessionId);
    }
  }, [newChatSessionId, onSessionChange]);

  const startGeneration = useCallback(
    async (completedSessionId: string) => {
      queryClient.setQueryData(
        CHAT_BOT.FLOW_STATUS(flowType),
        (prev: FlowStatusFromAPI) => {
          return {
            ...prev,
            status: FLOW_STATUS.IN_PROGRESS,
            run_method: {},
          };
        }
      );
      try {
        await MachineLearningService.completeChatSession(completedSessionId);
        await MachineLearningService.startFlow({
          type: flowType,
          session_id: completedSessionId,
        });
        setStartedGeneration(true);
        deferExecution(() => {
          refetchFlowStatus();
        });
      } catch (error: any) {
        const errorMessage = error.response?.data?.already_running
          ? t('A flow is already in progress, please try again later')
          : t('Error starting generation');
        showToast({
          variant: toastVariant.FAILURE,
          message: errorMessage,
        });
        onError?.(error);
      }
    },
    [queryClient, flowType, t, showToast, onError, refetchFlowStatus]
  );

  const startGenerationMutation = useMutation({
    mutationFn: startGeneration,
  });

  const showBusinessGenerationSummary = useMemo(
    () =>
      (startGenerationMutation.isPending && !inModal) ||
      (flowStatus?.status === FLOW_STATUS.COMPLETED && !inModal) ||
      (flowStatus?.status === FLOW_STATUS.IN_PROGRESS && !inModal) ||
      (flowStatus?.status === FLOW_STATUS.FAILED && !inModal),
    [inModal, flowStatus, startGenerationMutation.isPending]
  );

  const showFlowLoader = useMemo(
    () =>
      (startGenerationMutation.isPending ||
        startGenerationMutation.isSuccess ||
        flowStatus?.status === FLOW_STATUS.IN_PROGRESS) &&
      inModal &&
      !isNewChatSessionLoading,
    [inModal, flowStatus, isNewChatSessionLoading, startGenerationMutation]
  );

  const showChat = useMemo(() => {
    if (!inModal && (isFlowStatusLoading || !flowStatus || startedGeneration)) {
      return false;
    }
    return !showBusinessGenerationSummary && !showFlowLoader;
  }, [
    startedGeneration,
    showBusinessGenerationSummary,
    showFlowLoader,
    inModal,
    isFlowStatusLoading,
    flowStatus,
  ]);

  return (
    <div className="h-full w-full flex justify-center items-center">
      {showChat ? (
        <ChatBot
          header={header}
          showLogo={showLogo}
          headerLogoUrl={headerLogoUrl}
          entityName={entityName}
          errorMessage={
            isNewChatSessionError ? t('Error fetching chat session') : ''
          }
          chatSessionId={newChatSession?.id}
          fetchSession={MachineLearningService.getChatHistory}
          onCompleteSession={startGenerationMutation.mutate}
          getStreamingEndpoint={MachineLearningService.getStreamingEndpoint}
          generationVarToMessageHeaderMapper={
            generationVarToMessageHeaderMapper
          }
          eventListenerNames={streamEventListeners}
          onDeleteSession={deleteSessionMutation.mutate}
          newChatSessionLoading={
            isNewChatSessionLoading ||
            isFlowStatusLoading ||
            deleteSessionMutation.isPending ||
            isRefetchingChatSession ||
            isRefetchingFlowStatus
          }
          completeSessionLoading={
            flowStatus?.status === FLOW_STATUS.IN_PROGRESS ||
            startGenerationMutation.isPending
          }
          renderMarkdown={renderMarkdown}
          sampleIdeas={sampleIdeas}
        />
      ) : null}
      {showBusinessGenerationSummary ? (
        <BusinessGenerationSummary
          isMobile={isMobile}
          isStartGenerationLoading={isFlowStatusLoading}
          flowStatusMetadata={flowStatus?.run_metadata}
          labels={flowStatus?.labels}
          flowStatus={flowStatus?.status}
          createdAt={flowStatus?.created}
        />
      ) : null}
      {showFlowLoader ? <Indicator messages={pendingMessages} /> : null}
    </div>
  );
};
